import {gql} from '@apollo/client';
import {DocumentNode} from 'graphql';
import reactUpdate from 'immutability-helper';

import {assert, assertAndContinue} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {ELN_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {doQuerySafe} from 'graphql-client/ApolloWrappers';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {Selector} from 'notebook/ELNConstants';
import {
  CreateMixtureGroupExtras,
  MixtureGroupMetadata,
} from 'notebook/integrations/registry/MixtureGroupConstants';
import {
  MixtureIngredientsStructuredTable,
  MixtureIngredientsStructuredTableMetadata,
  MixtureIngredientsStructuredTableRowMetadata,
} from 'notebook/integrations/registry/MixtureIngredientsStructuredTableAdapter';
import MixtureMetadataStructuredTableAdapter, {
  MixtureMetadataStructuredTable,
} from 'notebook/integrations/registry/MixtureMetadataStructuredTableAdapter';
import {MixtureNoteLinkedGroupAdapter} from 'notebook/integrations/registry/MixtureNoteLinkedGroupAdapter';
import {
  getMixtureIngredientsStructuredTableAdapter,
  getMixtureMetadataStructuredTableAdapter,
} from 'notebook/integrations/registry/MixtureTableAdapters';
import {
  MixtureRefreshIngredientsQuery,
  MixtureRefreshIngredientsQueryVariables,
} from 'notebook/notes/groups/action-functions/__generated/MixtureRefreshIngredientsQuery';
import makeSimpleGroupActionFunction from 'notebook/notes/groups/action-functions/makeSimpleGroupActionFunction';
import {
  refreshContainedNotePart,
  refreshEntitiesInContainedNotePart,
} from 'notebook/notes/groups/action-functions/refreshNoteLinkedGroupActionFunction';
import {
  GroupActionFunctionParameters,
  NoteLinkedGroupKind,
} from 'notebook/notes/groups/NoteLinkedGroupConstants';
import {findNotePartForStructuredTable} from 'notebook/notes/groups/util/ContainedNotePartUtil';
import {ACTION_REFRESH} from 'notebook/store/VersioningConstants';
import {performClientSideWriteAction} from 'notebook/structured-tables/action-functions/PerformActionFunctions';
import {
  addTableRefreshWarnings,
  EDITS_IN_UNSUBMITTED_ROWS_WARNING,
  FORMULA_PRESENT_WARNING,
  RefreshWarningModal,
  UNSUBMITTED_CHANGES_IN_SUBMITTED_ROWS_WARNING,
} from 'notebook/structured-tables/action-functions/refreshTableActionFunction';
import {ResyncOptionsModal} from 'notebook/structured-tables/action-functions/ResyncOptionsModal';
import {
  LastSyncedUpstreamValue,
  StructuredTableAdapter,
  StructuredTableKind,
} from 'notebook/structured-tables/StructuredTableConstants';
import getInitialCellValuesForColumn from 'notebook/structured-tables/util/getInitialCellValuesForColumn';
import getInsertableColumnInfos from 'notebook/structured-tables/util/getInsertableColumnInfos';
import {EMPTY_PLACEHOLDER_INSERTION_CONTEXT} from 'notebook/structured-tables/util/StructuredTableInsertionContext';
import updateTableWithColumnUpdatesAndCells from 'notebook/structured-tables/util/updateTableWithColumnUpdatesAndCells';
import excess from 'notebook/tables/excess';
import {EvaluatedCell, OutputTableMap} from 'notebook/tables/excess/Evaluator';
import {handleRefreshStructuredTable} from 'notebook/tables/refresh-table-data/RefreshTableData';
import {
  InsertableColumnInfo,
  RESYNC_TABLE_LABEL,
  TableColumnType,
  TableData,
} from 'notebook/tables/TableConstants';
import {NotePart, NotePartWithItem} from 'notebook/util/NoteUtilConstants';
import {SchemaFieldSelector} from 'schemas/SchemaFieldSelectorConstants';
import legacyRunModal from 'ui-library/legacyRunModal';

type IngredientsGQL = ExtractByTypename<
  MixtureRefreshIngredientsQuery['mixture'],
  'MixtureNode'
>['ingredients'][number];

export const MIXTURE_REFRESH_INGREDIENTS_QUERY: DocumentNode = gql`
  query MixtureRefreshIngredientsQuery($mixtureId: StrictString!) @skipAudit @engComponent(name: REGISTRY) {
    mixture(id: $mixtureId) {
      ... on MixtureNode {
        ...MixtureSpecificAttributesGraphQL
        ingredients {
          ...IngredientGraphQL
          componentBioentity {
            id
            displayLabel
          }
          parentIngredientAmount
          parentIngredientId
        }
      }
      ... on InaccessibleInfoNode {
        ...InaccessibleInfoGraphQL
      }
    }
  }
  ${getFragmentWrapper('MixtureSpecificAttributes').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

async function fetchIngredientsForMixture(mixtureId: string): Promise<Array<IngredientsGQL> | null> {
  const data = await doQuerySafe<MixtureRefreshIngredientsQuery, MixtureRefreshIngredientsQueryVariables>(
    {query: MIXTURE_REFRESH_INGREDIENTS_QUERY},
    {variables: {mixtureId}, fetchPolicy: 'network-only'}
  );
  if (isInaccessible(data.mixture)) {
    return null;
  }
  return data.mixture.ingredients;
}

/**
 * Returns an updated MixtureIngredientsStructuredTable that has been synchronized with the
 * given array of Ingredients.  It uses the row's ingredientId stored in the metadata to match.
 * If there is no matching ingredient in the array, then the row is removed.  If an
 * ingredient is present in the array but not present in the table, then a new, blank row is
 * inserted into the table.
 *
 * @param adapter - the mixture ingredients table adapter
 * @param table - the ingredients table to update
 * @param ingredients - the list of ingredients to sync
 */
function replaceTableRowsFromLiveIngredients(
  adapter: StructuredTableAdapter<
    MixtureIngredientsStructuredTableMetadata,
    MixtureIngredientsStructuredTableRowMetadata
  >,
  table: MixtureIngredientsStructuredTable,
  ingredients: Array<IngredientsGQL>
): MixtureIngredientsStructuredTable {
  const newRows = ingredients.map((ingredient, rowIndex) => {
    const existingRow = table.rows.find(({metadata}) => metadata.ingredientId === ingredient.id);
    return (
      existingRow ?? {
        ...adapter.getBlankRow(table.metadata, rowIndex, table.columns, {
          ingredientId: ingredient.id,
          parentIngredientId: ingredient.parentIngredientId,
          parentIngredientAmount: ingredient.parentIngredientAmount
            ? {
                numericValue: Number(ingredient.parentIngredientAmount),
                stringValue: ingredient.parentIngredientAmount,
              }
            : null,
          isCatalogIdentifierInherited: ingredient.isCatalogIdentifierInherited,
        }),
        // Refreshed rows should always be finalized as they are coming from a submitted mixture
        isFinalized: true,
      }
    );
  });
  return {
    ...table,
    rows: newRows,
  };
}

function refreshIngredientsTableNotePart({
  entryOrTemplateId,
  evaluatedTableMap,
  getIdFromCidFn,
  getItemWithUpdatedContent,
  ingredientTableNotePart,
  mixtureId,
}: {
  entryOrTemplateId: string;
  evaluatedTableMap: OutputTableMap;
  getIdFromCidFn: (id: string) => string | number | undefined;
  getItemWithUpdatedContent: (oldContainedNotePart: NotePart, content: object) => NotePart;
  ingredientTableNotePart: NotePartWithItem<MixtureIngredientsStructuredTable>;
  mixtureId: string;
}): Promise<NotePart> {
  // eslint-disable-next-line no-async-promise-executor -- Disabling for now, but worth fixing
  return new Promise(async (resolve) => {
    const ingredients = await fetchIngredientsForMixture(mixtureId);

    if (ingredients == null) {
      // Don't do anything if we don't have permission to access the mixture
      resolve(ingredientTableNotePart);
      return;
    }

    const ingredientTable = ingredientTableNotePart.item;
    const ingredientsAdapter = getMixtureIngredientsStructuredTableAdapter();
    const updatedTable = replaceTableRowsFromLiveIngredients(
      ingredientsAdapter,
      ingredientTable,
      ingredients
    );

    const onUpdateTableCallback = (
      index: number,
      updatedContent: {
        table: TableData;
        lastSyncedUpstreamValuesByRow?: Array<Array<LastSyncedUpstreamValue>>;
      },
      selectors?: Array<Selector>
    ): void => {
      const updatedIngredientsNotePart = getItemWithUpdatedContent(ingredientTableNotePart, updatedContent);
      resolve(updatedIngredientsNotePart);
    };

    await handleRefreshStructuredTable({
      structuredTable: updatedTable,
      structuredTableAdapter: ingredientsAdapter,
      entryOrTemplateId,
      computedTable: evaluatedTableMap[updatedTable.name],
      onUpdateTable: updateTableWithColumnUpdatesAndCells(
        // The index is ignored as we thread the update back through the group
        0,
        ingredientsAdapter.toTable(updatedTable),
        getIdFromCidFn,
        onUpdateTableCallback,
        (_) => new Set()
      ),
    });
  });
}

function isTagValueInsertableColumnInfo(insertableColumnInfo: InsertableColumnInfo): boolean {
  return insertableColumnInfo.columnConstraints?.columnDefinition?.type === TableColumnType.TAG_VALUE;
}

/**
 * Adds any missing Schema Field columns to the MixtureMetadataStructuredTable.
 * These columns will be added directly before the MEASUREMENT_AMOUNT column.
 * The MEASUREMENT_AMOUNT and UNIT_SELECTOR columns need to always be the last
 * columns for the MixtureMetadataStructuredTable since those columns are
 * wrapped onto a new line.
 *
 * The insertion logic for the table does not shift any formulas as we don't
 * currently support cross-table formulas on NoteLinkedGroups.
 *
 * This method is a private method and only exported so we can test it easily.
 */
export function _addMissingColumns({
  entryOrTemplateId,
  adapter,
  mixtureMetadataStructuredTable,
  selectorsById,
  computedTable,
}: {
  entryOrTemplateId: string;
  adapter: MixtureMetadataStructuredTableAdapter;
  mixtureMetadataStructuredTable: MixtureMetadataStructuredTable;
  selectorsById: {[selectorId: string]: SchemaFieldSelector} | null;
  computedTable: Array<Array<EvaluatedCell>>;
}): MixtureMetadataStructuredTable {
  const tableName = mixtureMetadataStructuredTable.name;

  // Only add schema field columns which we don't currently have
  const insertableColumnInfos = getInsertableColumnInfos({
    adapter,
    structuredTable: mixtureMetadataStructuredTable,
    entryId: entryOrTemplateId,
    selectorsById,
    structuredTableInsertionContext: EMPTY_PLACEHOLDER_INSERTION_CONTEXT,
  }).filter((insertableColumnInfo) => isTagValueInsertableColumnInfo(insertableColumnInfo));

  // The MixtureMetadataTable always needs the measurement and units columns last, so insert before those.
  const measurementColumnIndex = mixtureMetadataStructuredTable.columns.findIndex(
    ({columnType}) => columnType === TableColumnType.MEASUREMENT_AMOUNT
  );
  assert(measurementColumnIndex !== -1, 'No measurement column present');

  // TODO(BNCH-15513): Since we don't support X-table formulas for NLGs, we can just use the TableOp directly
  // We discard the selection box as we don't want to set the selection to the columns we just added
  const {tables} = insertableColumnInfos.reduce(
    ({tables}, insertableColumnInfo) =>
      excess.ops.wrapTableOpForSingleDoc((table, documentName) =>
        excess.ops.insertColumnsLeft(
          table,
          documentName,
          measurementColumnIndex,
          measurementColumnIndex,
          insertableColumnInfo,
          getInitialCellValuesForColumn(
            adapter,
            mixtureMetadataStructuredTable,
            insertableColumnInfo,
            computedTable
          )
        )
      )(tables, {[tableName]: computedTable}, tableName),
    {
      tables: {[tableName]: adapter.toTable(mixtureMetadataStructuredTable)},
    }
  );
  return adapter.fromTable(tables[tableName]);
}

async function refreshMixtureNoteLinkedGroup(
  params: GroupActionFunctionParameters<MixtureGroupMetadata, CreateMixtureGroupExtras>
): Promise<void> {
  const {
    entryOrTemplateId,
    getItemWithUpdatedContent,
    noteLinkedGroup,
    noteLinkedGroupAdapter,
    updateGroup,
    evaluatedTableMap,
    getIdFromCidFn,
    selectorsById,
  } = params;
  const mixtureGroupAdapter = noteLinkedGroupAdapter as MixtureNoteLinkedGroupAdapter;
  const metadataTable = mixtureGroupAdapter.getMixtureMetadataStructuredTable(noteLinkedGroup);
  const ingredientsTable = mixtureGroupAdapter.getMixtureIngredientsStructuredTable(noteLinkedGroup);
  const mixtureTableAdapter = getMixtureMetadataStructuredTableAdapter();
  const computedMetadataTable = evaluatedTableMap[metadataTable.name];
  const computedIngredientsTable = evaluatedTableMap[ingredientsTable.name];

  // Checking for warnings in metadata table content
  const [
    metadataUnsubmittedChangesInSubmittedRow,
    metadataPossibleEditsInUnsubmittedRow,
    metadataFormulaPresent,
  ] = addTableRefreshWarnings(metadataTable, computedMetadataTable);
  // Checking for warnings in ingredients table content
  const [
    ingredientsUnsubmittedChangesInSubmittedRow,
    ingredientsPossibleEditsInUnsubmittedRow,
    ingredientsFormulaPresent,
  ] = addTableRefreshWarnings(ingredientsTable, computedIngredientsTable);
  const unsubmittedChangesInSubmittedRow =
    metadataUnsubmittedChangesInSubmittedRow || ingredientsUnsubmittedChangesInSubmittedRow;
  const possibleEditsInUnsubmittedRows =
    metadataPossibleEditsInUnsubmittedRow || ingredientsPossibleEditsInUnsubmittedRow;
  const formulaPresent = metadataFormulaPresent || ingredientsFormulaPresent;
  const shouldShowResyncModal = formulaPresent || unsubmittedChangesInSubmittedRow;

  const refreshEntitiesTables = async (): Promise<void> => {
    assert(noteLinkedGroup.kind === NoteLinkedGroupKind.MIXTURE_GROUP, 'Unexpected note linked group kind', {
      kind: noteLinkedGroup.kind,
    });

    const mixtureId = mixtureTableAdapter.getMixtureId(metadataTable, computedMetadataTable);

    if (!assertAndContinue(mixtureId != null, 'Mixture not present on table')) {
      return;
    }

    const updatedMixtureMetadataNotePart: NotePartWithItem<MixtureMetadataStructuredTable> =
      await refreshEntitiesInContainedNotePart({
        containedNotePart: findNotePartForStructuredTable<MixtureMetadataStructuredTable>(
          noteLinkedGroup,
          StructuredTableKind.MIXTURE_METADATA
        )!,
        computedTable: computedMetadataTable,
        getIdFromCidFn,
        entryOrTemplateId,
        getItemWithUpdatedContent,
      });
    const updatedWithMetadataNotePart = await noteLinkedGroupAdapter.reduceGroup(
      noteLinkedGroup,
      getItemWithUpdatedContent(updatedMixtureMetadataNotePart, {
        item: _addMissingColumns({
          adapter: mixtureTableAdapter,
          entryOrTemplateId,
          mixtureMetadataStructuredTable: updatedMixtureMetadataNotePart.item,
          selectorsById,
          computedTable: computedMetadataTable,
        }),
      })
    );

    const updatedIngredientNotePart = await refreshEntitiesInContainedNotePart({
      containedNotePart: findNotePartForStructuredTable<MixtureIngredientsStructuredTable>(
        updatedWithMetadataNotePart,
        StructuredTableKind.MIXTURE_INGREDIENTS
      )!,
      computedTable: computedIngredientsTable,
      getIdFromCidFn,
      entryOrTemplateId,
      getItemWithUpdatedContent,
    });
    const updatedWithIngredientNotePart = reactUpdate(updatedWithMetadataNotePart, {
      containedNoteParts: {
        $splice: [[1, 1, updatedIngredientNotePart]],
      },
    });

    updateGroup(updatedWithIngredientNotePart);
  };

  const refreshTables = async (): Promise<void> => {
    assert(noteLinkedGroup.kind === NoteLinkedGroupKind.MIXTURE_GROUP, 'Unexpected note linked group kind', {
      kind: noteLinkedGroup.kind,
    });

    // Refreshing should be disabled if there is no mixture
    const mixtureId = mixtureTableAdapter.getMixtureId(metadataTable, computedMetadataTable);

    if (!assertAndContinue(mixtureId != null, 'Mixture not present on table')) {
      return;
    }

    const updatedMixtureMetadataNotePart: NotePartWithItem<MixtureMetadataStructuredTable> =
      await refreshContainedNotePart({
        containedNotePart: findNotePartForStructuredTable<MixtureMetadataStructuredTable>(
          noteLinkedGroup,
          StructuredTableKind.MIXTURE_METADATA
        )!,
        evaluatedTableMap,
        getIdFromCidFn,
        entryOrTemplateId,
        getItemWithUpdatedContent,
      });
    const updatedWithMetadataNotePart = await noteLinkedGroupAdapter.reduceGroup(
      noteLinkedGroup,
      getItemWithUpdatedContent(updatedMixtureMetadataNotePart, {
        item: _addMissingColumns({
          adapter: mixtureTableAdapter,
          entryOrTemplateId,
          mixtureMetadataStructuredTable: updatedMixtureMetadataNotePart.item,
          selectorsById,
          computedTable: computedMetadataTable,
        }),
      })
    );

    const updatedIngredientNotePart = await refreshIngredientsTableNotePart({
      ingredientTableNotePart: findNotePartForStructuredTable<MixtureIngredientsStructuredTable>(
        updatedWithMetadataNotePart,
        StructuredTableKind.MIXTURE_INGREDIENTS
      )!,
      evaluatedTableMap,
      getItemWithUpdatedContent,
      getIdFromCidFn,
      entryOrTemplateId,
      mixtureId: mixtureId!,
    });
    // Do a simple update instead of using reduceGroup again after refreshing the ingredients table because:
    // - It will produce incorrect behavior in the case where the parent mixture value has been updated on the
    //   mixture in the DB: In that case, we want the ingredients table to receive an exact copy of the new
    //   inherited ingredients from the mixture, rather than newly-created ones from the reducer
    // - Only changes to the metadata note part trigger any mixture group reducer logic, so there's no risk of
    //   missing out on logic that should have been executed
    const updatedWithIngredientNotePart = reactUpdate(updatedWithMetadataNotePart, {
      containedNoteParts: {
        $splice: [[1, 1, updatedIngredientNotePart]],
      },
    });

    updateGroup(updatedWithIngredientNotePart);
  };

  const runRefresh = async (
    doPerformAction: () => Promise<void>,
    loadingScreenMessage: string
  ): Promise<void> => {
    await performClientSideWriteAction({
      entryOrTemplateId,
      loadingScreenMessage,
      autoSaverAction: ACTION_REFRESH,
      doPerformAction,
      analyticsEvent: ELN_ANALYTICS_ACTIONS.REFRESHED_NOTE_LINKED_GROUP,
      analyticsMetadata: {
        noteLinkedGroupKind: noteLinkedGroup.kind,
      },
    });
  };

  const isNewResyncModalEnabled = BenchlingConfig.ELN_ENABLE_STRUCTURED_TABLE_NEW_RESYNC_MODAL;

  if (isNewResyncModalEnabled) {
    if (shouldShowResyncModal) {
      legacyRunModal(ResyncOptionsModal, {
        unsubmittedChangesInSubmittedRow,
        possibleEditsInUnsubmittedRows,
        formulaPresent,
        onRequestKeepFormulas: async () => {
          await runRefresh(refreshEntitiesTables, 'Refreshing...');
        },
        onRequestRemoveFormulas: async () => {
          await runRefresh(refreshTables, 'Re-syncing...');
        },
      });
    } else {
      await runRefresh(refreshEntitiesTables, 'Refreshing...');
    }
  } else {
    const legacyWarnings: Array<string> = [];
    if (unsubmittedChangesInSubmittedRow) {
      legacyWarnings.push(UNSUBMITTED_CHANGES_IN_SUBMITTED_ROWS_WARNING);
    }
    if (possibleEditsInUnsubmittedRows) {
      legacyWarnings.push(EDITS_IN_UNSUBMITTED_ROWS_WARNING);
    }
    if (formulaPresent) {
      legacyWarnings.push(FORMULA_PRESENT_WARNING);
    }
    if (legacyWarnings.length) {
      legacyRunModal(RefreshWarningModal, {
        warnings: legacyWarnings,
        onRequestSubmit: async () => {
          await runRefresh(refreshTables, 'Re-syncing...');
        },
      });
    } else {
      await runRefresh(refreshTables, 'Refreshing...');
    }
  }
}

/**
 * Refreshing a Mixture NoteLinkedGroup should be disabled for the following conditions:
 * - the entry is locked for some reason, i.e. read-only
 * - there is no mixture present in the metadata table
 *
 * @param params
 */
function isRefreshDisabled(
  params: GroupActionFunctionParameters<MixtureGroupMetadata, CreateMixtureGroupExtras>
): boolean {
  if (params.locked) {
    return true;
  }

  const {noteLinkedGroupAdapter, noteLinkedGroup, evaluatedTableMap} = params;

  const groupAdapter = noteLinkedGroupAdapter as MixtureNoteLinkedGroupAdapter;
  const metadataTable = groupAdapter.getMixtureMetadataStructuredTable(noteLinkedGroup);
  const metadataAdapter = getMixtureMetadataStructuredTableAdapter();
  return metadataAdapter.getMixtureId(metadataTable, evaluatedTableMap[metadataTable.name] ?? null) == null;
}

const refreshMixtureNoteLinkedGroupActionFunction = makeSimpleGroupActionFunction<
  MixtureGroupMetadata,
  CreateMixtureGroupExtras
>({
  label: RESYNC_TABLE_LABEL,
  icon: 'arrows-rotate',
  iconAltText: 'Refresh',
  onInvoke: refreshMixtureNoteLinkedGroup,
  getDisabled: isRefreshDisabled,
});

export default refreshMixtureNoteLinkedGroupActionFunction;
